// types
import { FieldSource, FieldType, IField, IRiskTemplate } from 'types/graphql'
import { RiskFilterTypes, relatedTypesBySource, RiskSpecialRelatedTypesText } from './constants'
// constants
import { SELECT_GROUP_LABELS } from 'constants/forms'
import { FORM_SELECT_SECURITY_OPTIONS } from 'constants/security'
// utils
import { isEmpty } from 'lodash'
import { getEntitySelectOptions, getOptionSource } from 'utils/fields'

const hasSource = (fieldSources: FieldSource[], targetSources = [FieldSource.Users, FieldSource.Positions]): boolean =>
  fieldSources.some((source) => targetSources.includes(source))

const getLookupFields = (fields: IField[], currentFieldId: string): IField[] =>
  fields.filter(
    (field) => field.id !== currentFieldId && field.fieldType === FieldType.Lookup && hasSource(field.source)
  )

export const getOptionsBySource = (fields: IField[], fieldId: string, sources: FieldSource[]) => {
  const lookupFields = getLookupFields(fields, fieldId)
  const sourcesToExecute = sources.filter((source) =>
    [FieldSource.Users, FieldSource.Positions, FieldSource.BusinessUnits].includes(source)
  )

  if (!lookupFields.length || !sourcesToExecute.length) {
    return
  }

  return {
    label: SELECT_GROUP_LABELS.FIELDS,
    options: lookupFields.flatMap(({ id, label }) =>
      sourcesToExecute.flatMap((source) => {
        const baseTypes = relatedTypesBySource[source] || []

        return baseTypes.map((enumValue) => ({
          relatedFieldId: id,
          value: enumValue,
          label: `${label} (${RiskSpecialRelatedTypesText[enumValue]})`
        }))
      })
    )
  }
}

export const getOptions =
  ({
    field,
    isFilter,
    riskTemplate,
    isRiskTemplate
  }: {
    field: IField
    isFilter: boolean
    riskTemplate: IRiskTemplate
    isRiskTemplate: boolean
  }) =>
  ({ data }: Record<string, any>) => {
    if (isEmpty(data)) return []

    let options = getEntitySelectOptions(field)({ data }) || []

    const isUserOrPositionField = hasSource(field.source)

    if (isFilter) {
      if (hasSource(field.source, [FieldSource.BusinessUnits])) {
        options = [
          {
            label: 'My Business Unit',
            value: RiskFilterTypes.BUSINESS_UNITS,
            entity: FieldSource.BusinessUnits
          },
          ...options
        ]
      }

      if (isUserOrPositionField) {
        options = [
          {
            label: 'Me',
            value: RiskFilterTypes.ME,
            entity: getOptionSource(field.source)
          },
          ...options
        ]
      }
    }

    if (isRiskTemplate) {
      const fieldsOptions = getOptionsBySource(
        riskTemplate.fields.filter((item: IField & { fieldLinkId?: string }) => !item.fieldLinkId),
        field.id,
        field.source
      )

      if (fieldsOptions) {
        options = [fieldsOptions, ...options]
      }
    }

    if (isRiskTemplate && isUserOrPositionField) {
      options = [...FORM_SELECT_SECURITY_OPTIONS, ...options]
    }

    return options
  }
